import { SupabaseEdge, Logger } from '@footsteps/shared';
import type {
  AddressByIdResponse,
  AddressLookupResponse,
  WozComplaintClassificationResponse,
  WozFormatComplaintResult,
  WozFormatComplaintsPayload,
  WozGetFlowPayload,
  WozGetFlowResponse,
  WozCaseListItem,
  WozCaseDetailResponse,
  CaseAttachmentResponse,
  SignedUrlResponse,
} from './supabase-api.types';

/**
 * Centralised service for all Supabase Edge Function calls.
 * Each method maps to a single edge function endpoint.
 */
export class SupabaseApiService {
  private static instance: SupabaseApiService | null = null;

  private readonly logger: Logger;
  private readonly edge: SupabaseEdge;

  private constructor() {
    this.logger = new Logger(this.constructor.name);
    this.edge = SupabaseEdge.getInstance();
  }

  static getInstance(): SupabaseApiService {
    if (!SupabaseApiService.instance) {
      SupabaseApiService.instance = new SupabaseApiService();
    }
    return SupabaseApiService.instance;
  }

  /**
   * Look up addresses via the PDOK locatieserver.
   */
  async lookupAddress(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressLookupResponse> {
    try {
      const data = await this.edge.request<AddressLookupResponse>({
        path: 'api/address/lookup',
        body: { postalCode, houseNumber, houseNumberAddition: houseNumberAddition || undefined },
      });

      return data ?? { addresses: [], status: 'not_found' };
    } catch (error) {
      this.logger.error('Address lookup failed', error);
      throw new Error('Adres opzoeken mislukt. Probeer het opnieuw.');
    }
  }

  /**
   * Fetch merged address + kadaster data by address point ID.
   * Returns a partial WozCaseEntity shape.
   */
  async fetchAddressById(addressId: string): Promise<AddressByIdResponse> {
    try {
      const data = await this.edge.request<AddressByIdResponse>({
        path: `api/address/${encodeURIComponent(addressId)}`,
        method: 'POST',
      });

      if (!data) {
        throw new Error('Geen objectgegevens gevonden.');
      }

      return data;
    } catch (error) {
      if (error instanceof Error && error.message === 'Geen objectgegevens gevonden.') {
        throw error;
      }
      this.logger.error('Address fetch failed', error);
      throw new Error('Objectgegevens ophalen mislukt.');
    }
  }

  /**
   * Send the user's free-text complaint to the AI classification edge function
   * and return structured classification data.
   */
  async classifyComplaints(text: string): Promise<WozComplaintClassificationResponse | null> {
    try {
      return await this.edge.request<WozComplaintClassificationResponse>({
        path: 'api/woz/complaint-classification',
        body: { text },
      });
    } catch (error) {
      this.logger.error('AI classification failed', error);
      throw new Error('Classificatie mislukt. Probeer het opnieuw.');
    }
  }

  /**
   * Fetch the question flow for a classified complaint.
   */
  async getFlow(payload: WozGetFlowPayload): Promise<WozGetFlowResponse | null> {
    try {
      return await this.edge.request<WozGetFlowResponse>({
        path: 'api/woz/get-flow',
        body: payload as unknown as Record<string, unknown>,
      });
    } catch (error) {
      this.logger.error('Get flow failed', error);
      throw new Error('Vragenlijst ophalen mislukt. Probeer het opnieuw.');
    }
  }

  /**
   * Format complaints by resolving questions/answers and generating
   * professional Dutch text via the AI edge function.
   */
  async formatComplaints(payload: WozFormatComplaintsPayload): Promise<WozFormatComplaintResult[]> {
    try {
      const data = await this.edge.request<WozFormatComplaintResult[]>({
        path: 'api/woz/format-complaints',
        body: payload as unknown as Record<string, unknown>,
      });
      return data ?? [];
    } catch (error) {
      this.logger.error('Format complaints failed', error);
      throw new Error('Bezwaren formatteren mislukt. Probeer het opnieuw.');
    }
  }

  // ── Case CRUD ────────────────────────────────────────────────────

  async createCase(payload: Record<string, unknown>): Promise<WozCaseDetailResponse> {
    try {
      const data = await this.edge.request<WozCaseDetailResponse>({
        path: 'api/woz/case',
        body: payload,
      });
      if (!data) throw new Error('Geen bezwaar aangemaakt.');
      return data;
    } catch (error) {
      this.logger.error('Create case failed', error);
      throw new Error('Bezwaar aanmaken mislukt. Probeer het opnieuw.');
    }
  }

  async updateCase(caseId: string, payload: Record<string, unknown>): Promise<WozCaseDetailResponse> {
    try {
      const data = await this.edge.request<WozCaseDetailResponse>({
        path: `api/woz/case/${encodeURIComponent(caseId)}`,
        method: 'POST',
        body: payload,
      });
      if (!data) throw new Error('Zaak niet gevonden.');
      return data;
    } catch (error) {
      this.logger.error('Update case failed', error);
      throw new Error('Zaak bijwerken mislukt. Probeer het opnieuw.');
    }
  }

  async fetchCases(): Promise<WozCaseListItem[]> {
    try {
      const data = await this.edge.request<WozCaseListItem[]>({
        path: 'api/woz/cases',
        method: 'GET',
      });
      return data ?? [];
    } catch (error) {
      this.logger.error('Fetch cases failed', error);
      throw new Error('Zaken ophalen mislukt. Probeer het opnieuw.');
    }
  }

  async fetchCase(caseId: string): Promise<WozCaseDetailResponse> {
    try {
      const data = await this.edge.request<WozCaseDetailResponse>({
        path: `api/woz/case/${encodeURIComponent(caseId)}`,
        method: 'GET',
      });
      if (!data) throw new Error('Zaak niet gevonden.');
      return data;
    } catch (error) {
      this.logger.error('Fetch case failed', error);
      throw new Error('Zaak ophalen mislukt. Probeer het opnieuw.');
    }
  }

  /**
   * Restore a case from the database in the WozCaseEntity shape
   * so the frontend can construct a WozCaseEntity and resume the flow.
   */
  async restoreCase(caseId: string): Promise<Record<string, unknown>> {
    try {
      const data = await this.edge.request<Record<string, unknown>>({
        path: `api/woz/case/restore/${encodeURIComponent(caseId)}`,
        method: 'GET',
      });
      if (!data) throw new Error('Zaak niet gevonden.');
      return data;
    } catch (error) {
      this.logger.error('Restore case failed', error);
      throw new Error('Zaak herstellen mislukt. Probeer het opnieuw.');
    }
  }

  async submitCase(
    caseId: string,
    payload: { shareConsent: boolean; contactConsent: boolean }
  ): Promise<WozCaseDetailResponse> {
    try {
      const data = await this.edge.request<WozCaseDetailResponse>({
        path: `api/woz/case/${encodeURIComponent(caseId)}/submit`,
        method: 'POST',
        body: payload,
      });
      if (!data) throw new Error('Indienen mislukt.');
      return data;
    } catch (error) {
      this.logger.error('Submit case failed', error);
      throw new Error('Bezwaar indienen mislukt. Probeer het opnieuw.');
    }
  }

  async deleteComplaint(caseId: string, complaintId: string): Promise<void> {
    try {
      await this.edge.request({
        path: `api/woz/case/${encodeURIComponent(caseId)}/complaint/${encodeURIComponent(complaintId)}`,
        method: 'DELETE',
      });
    } catch (error) {
      this.logger.error('Delete complaint failed', error);
      throw new Error('Klacht verwijderen mislukt. Probeer het opnieuw.');
    }
  }

  // ── Attachments ──────────────────────────────────────────────────

  async uploadAttachment(formData: FormData): Promise<CaseAttachmentResponse> {
    try {
      const data = await this.edge.request<CaseAttachmentResponse>({
        path: 'api/attachment',
        body: formData,
        timeoutMs: 30_000,
      });
      if (!data) throw new Error('Upload mislukt.');
      return data;
    } catch (error) {
      this.logger.error('Upload attachment failed', error);
      throw new Error('Bestand uploaden mislukt. Probeer het opnieuw.');
    }
  }

  async listAttachments(caseId: string): Promise<CaseAttachmentResponse[]> {
    try {
      const data = await this.edge.request<CaseAttachmentResponse[]>({
        path: `api/attachments/${encodeURIComponent(caseId)}`,
        method: 'GET',
      });
      return data ?? [];
    } catch (error) {
      this.logger.error('List attachments failed', error);
      throw new Error('Bijlagen ophalen mislukt. Probeer het opnieuw.');
    }
  }

  async getAttachmentSignedUrl(attachmentId: string): Promise<string> {
    try {
      const data = await this.edge.request<SignedUrlResponse>({
        path: `api/attachment/${encodeURIComponent(attachmentId)}/url`,
        method: 'GET',
      });
      if (!data?.signedUrl) throw new Error('URL ophalen mislukt.');
      return data.signedUrl;
    } catch (error) {
      this.logger.error('Get signed URL failed', error);
      throw new Error('Download link ophalen mislukt. Probeer het opnieuw.');
    }
  }

  async updateAttachmentDescription(attachmentId: string, description: string | null): Promise<CaseAttachmentResponse> {
    try {
      const data = await this.edge.request<CaseAttachmentResponse>({
        path: `api/attachment/${encodeURIComponent(attachmentId)}`,
        method: 'PATCH',
        body: { description },
      });
      if (!data) throw new Error('Bijwerken mislukt.');
      return data;
    } catch (error) {
      this.logger.error('Update attachment description failed', error);
      throw new Error('Beschrijving bijwerken mislukt. Probeer het opnieuw.');
    }
  }

  async deleteAttachment(attachmentId: string): Promise<void> {
    try {
      await this.edge.request({
        path: `api/attachment/${encodeURIComponent(attachmentId)}`,
        method: 'DELETE',
      });
    } catch (error) {
      this.logger.error('Delete attachment failed', error);
      throw new Error('Bijlage verwijderen mislukt. Probeer het opnieuw.');
    }
  }
}
